import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import { benchmarkUrl } from '../../lib/constants/benchmark';
import './local-ai-catchup.css';
import {
  PROVIDERS, PROVIDER_MAP, FRONTIER, GRID_INDEX, YEAR_TICKS, PLOT,
  xOf, yOf, rOf, starOfProvider,
  resolveCompare, perMonthLine, OPEN_PROVIDERS, DEFAULT_LOCAL_IDS, DEFAULT_OPEN_IDS, MODELS,
  MODEL_BY_ID, MODEL_HW, HARDWARE_TIERS,
  ROI_CLOUD_OPTIONS, DEFAULT_ROI_CLOUD_IDS, DEFAULT_ROI_LOCAL_IDS,
  type FrontierPoint, type ProviderId, type ModelPoint, type CompareItem,
} from './localAiCatchup.data';

const delayVar = (d: number): CSSProperties => ({ ['--d' as string]: `${d}s` } as CSSProperties);

/* The proprietary frontier reference (gold) — the "data center keeps climbing" line. */
const OPUS45 = 40.8;            // the open-weight target line
const FRONTIER_NOW = FRONTIER[FRONTIER.length - 1]; // Fable 5


export type DiagramMode = 'local' | 'openweight' | 'roi';

/* ── ROI chart constants (module-level, stable) ─────────────── */
const ROI_MAX_M = 50;          // x-axis: 50 M tokens/month
const ROI_MAX_COST = 600;      // y-axis: $600/month
const RP = { x0: 85, x1: 960, yBottom: 382, yTop: 44 };
const xM = (m: number) => RP.x0 + (m / ROI_MAX_M) * (RP.x1 - RP.x0);
const yC = (usd: number) => RP.yBottom - (Math.min(Math.max(usd, 0), ROI_MAX_COST) / ROI_MAX_COST) * (RP.yBottom - RP.yTop);
const ROI_Y_TICKS = [100, 200, 300, 400, 500, 600];
const ROI_X_TICKS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
const AMORT_OPTIONS = [12, 24, 36, 48, 60];

const pts = (ms: ModelPoint[]) => ms.map(m => `${xOf(m.date)},${yOf(m.index)}`).join(' ');
const areaPts = (ms: ModelPoint[]) => {
  if (!ms.length) return '';
  const b = PLOT.yBottom;
  return `${xOf(ms[0].date)},${b} ${pts(ms)} ${xOf(ms[ms.length - 1].date)},${b}`;
};

export function LocalAiCatchup() {
  const { t, i18n } = useTranslation('home');
  const c = (k: string, d?: string, o?: Record<string, unknown>) =>
    t(`localAiCatchup.${k}`, { defaultValue: d ?? k, ...o }) as string;

  const [mode, setMode] = useState<DiagramMode>('local');
  const [selectedLocal, setSelectedLocal] = useState<Set<string>>(new Set(DEFAULT_LOCAL_IDS));
  const [selectedOpen, setSelectedOpen] = useState<Set<string>>(new Set(DEFAULT_OPEN_IDS));
  const selected = mode === 'local' ? selectedLocal : selectedOpen;
  const setSelected = (fn: (prev: Set<string>) => Set<string>) => {
    if (mode === 'local') setSelectedLocal(fn);
    else setSelectedOpen(fn);
  };
  const clearSelected = () => {
    if (mode === 'local') setSelectedLocal(new Set());
    else setSelectedOpen(new Set());
  };
  const resetSelected = () => {
    if (mode === 'local') setSelectedLocal(new Set(DEFAULT_LOCAL_IDS));
    else setSelectedOpen(new Set(DEFAULT_OPEN_IDS));
  };
  const [showFrontier, setShowFrontier] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<ProviderId>>(new Set());
  const [inView, setInView] = useState(false);
  const [animEpoch, setAnimEpoch] = useState(0);
  const [showLabelsLocal, setShowLabelsLocal] = useState(true);
  const [showLabelsOpen, setShowLabelsOpen] = useState(false);
  const showLabels = mode === 'local' ? showLabelsLocal : showLabelsOpen;
  const setShowLabels = mode === 'local' ? setShowLabelsLocal : setShowLabelsOpen;

  /* ── ROI / Breakeven state ─────────────────────────────────── */
  const [amortMonths, setAmortMonths] = useState(36);
  const [roiLocalIds, setRoiLocalIds] = useState<string[]>(DEFAULT_ROI_LOCAL_IDS);
  const [roiCloudIds, setRoiCloudIds] = useState<string[]>(DEFAULT_ROI_CLOUD_IDS);
  const [roiFilterOpen, setRoiFilterOpen] = useState(false);

  const roiEntries = useMemo(() => {
    return roiLocalIds
      .map(id => {
        const m = MODEL_BY_ID[id];
        const tierId = MODEL_HW[id];
        const hw = tierId ? HARDWARE_TIERS[tierId] : undefined;
        const tco = hw ? (hw.gpuPriceUsd / amortMonths) + hw.monthlyPowerUsd + hw.monthlyMaintenanceUsd : null;
        return { id, model: m, hw, tierId, tco };
      })
      .filter((e): e is { id: string; model: ModelPoint; hw: typeof HARDWARE_TIERS[string]; tierId: string; tco: number } =>
        !!e.model && e.tco !== null && e.hw !== undefined);
  }, [roiLocalIds, amortMonths]);

  const roiClouds = useMemo(() =>
    roiCloudIds.map(id => ROI_CLOUD_OPTIONS.find(o => o.id === id)!).filter(Boolean),
    [roiCloudIds]);

  const rootRef = useRef<HTMLElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  /* ── tooltip + compare ─────────────────────────────────── */
  const [tip, setTip] = useState<{ x: number; y: number; m: CompareItem; below: boolean } | null>(null);
  const [compareIds, setCompareIds] = useState<string[]>(['f:Claude Opus 4.5', 'qwen-36-27']);

  const toggleCompareModel = (m: ModelPoint) => {
    setSelected(prev => { const s = new Set(prev); s.add(m.id); return s; }); // ensure visible
    setCompareIds(prev => {
      if (prev.includes(m.id)) return prev.filter(id => id !== m.id);
      if (prev.length >= 2) return [prev[1], m.id];
      return [...prev, m.id];
    });
  };
  const toggleCompareFrontier = (f: FrontierPoint) => {
    const fid = 'f:' + f.name;
    setCompareIds(prev => {
      if (prev.includes(fid)) return prev.filter(id => id !== fid);
      if (prev.length >= 2) return [prev[1], fid];
      return [...prev, fid];
    });
  };
  const compareModels = compareIds.map(id => resolveCompare(id)).filter(Boolean) as CompareItem[];

  /* Tooltip: show above the dot by default, but flip below if the dot is in the
     top 35% of the chart. Clamp x so the tooltip never escapes the chart bounds. */
  const showTip = (clientX: number, clientY: number, item: CompareItem) => {
    const r = chartRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = clientX - r.left;
    const py = clientY - r.top;
    const below = py < r.height * 0.35;          // flip below if near top
    const halfW = 95;                             // approx half tooltip width
    const x = Math.max(halfW + 4, Math.min(r.width - halfW - 4, px)); // clamp x
    setTip({ x, y: py, m: item, below });
  };
  const onDotHover = (e: React.MouseEvent, m: CompareItem) => showTip(e.clientX, e.clientY, m);
  const onFrontierHover = (e: React.MouseEvent, f: FrontierPoint) => {
    const item = resolveCompare('f:' + f.name);
    if (item) showTip(e.clientX, e.clientY, item);
  };
  const clearHover = () => setTip(null);

  /* ── selection helpers ─────────────────────────────────── */
  const has = (id: string) => selected.has(id);
  const toggleModel = (id: string) =>
    setSelected(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const addMany = (ids: string[], on: boolean) =>
    setSelected(prev => { const s = new Set(prev); ids.forEach(i => { if (on) s.add(i); else s.delete(i); }); return s; });

  const modeModelsOfProvider = (id: ProviderId) => modeModels.filter(m => m.provider === id);
  const providerActive = (id: ProviderId) => modeModelsOfProvider(id).some(m => selected.has(m.id));
  const toggleProvider = (id: ProviderId) => {
    const all = modeModelsOfProvider(id).map(m => m.id);
    if (providerActive(id)) addMany(all, false);
    else addMany(modeModelsOfProvider(id).filter(m => m.key).map(m => m.id), true);
  };
  const familyIds = (id: ProviderId, fam: string) =>
    modeModelsOfProvider(id).filter(m => m.family === fam).map(m => m.id);
  const familyActive = (id: ProviderId, fam: string) =>
    familyIds(id, fam).every(i => selected.has(i));

  /* ── derived chart data ────────────────────────────────── */
  // Models available in current mode: local = local-capable only; openweight = all open
  const modeModels = useMemo(() =>
    mode === 'local'
      ? MODELS.filter(m => PROVIDER_MAP[m.provider].deployMode !== 'cloud' && m.local)
      : MODELS.filter(m => PROVIDER_MAP[m.provider].deployMode !== 'cloud'),
  [mode]);

  const selByProvider = useMemo(() => {
    const map = new Map<ProviderId, ModelPoint[]>();
    for (const p of OPEN_PROVIDERS) {
      const ms = modeModels.filter(m => m.provider === p.id && selected.has(m.id));
      if (ms.length) map.set(p.id, ms);
    }
    return map;
  }, [selected, modeModels]);

  // Spread dots that are ≤8px apart vertically so every dot is hoverable independently.
  // Lines and labels use the same spread positions so they stay connected.
  const dotLayout = useMemo((): Map<string, number> => {
    type Dot = { id: string; cx: number; cy: number };
    const dots: Dot[] = [];
    for (const [, ms] of selByProvider.entries())
      for (const m of perMonthLine(ms))
        dots.push({ id: m.id, cx: xOf(m.date), cy: yOf(m.index) });

    const layout = new Map(dots.map(d => [d.id, d.cy]));
    if (dots.length === 0) return layout;

    const SPREAD = 20; // px between adjacent dots in a cluster
    dots.sort((a, b) => a.cx - b.cx || a.cy - b.cy);

    let i = 0;
    while (i < dots.length) {
      let j = i;
      while (j < dots.length && dots[j].cx - dots[i].cx <= 15) j++;
      // chain-link y-cluster within this x-group: consecutive dots within 8px
      let k = i;
      while (k < j) {
        let l = k + 1;
        while (l < j && dots[l].cy - dots[l - 1].cy < 8) l++;
        if (l > k + 1) {
          const cluster = dots.slice(k, l);
          const avg = cluster.reduce((s, d) => s + d.cy, 0) / cluster.length;
          cluster.forEach((d, idx) =>
            layout.set(d.id, avg + (idx - (cluster.length - 1) / 2) * SPREAD));
        }
        k = l;
      }
      i = j;
    }
    return layout;
  }, [selByProvider]);

  const activeProv = useMemo(() => {
    const withStar = OPEN_PROVIDERS.find(p => modeModels.some(m => m.provider === p.id && m.star && selected.has(m.id)));
    return withStar ?? OPEN_PROVIDERS.find(p => selByProvider.has(p.id)) ?? null;
  }, [selByProvider, selected, modeModels]);
  const activeStar = activeProv ? starOfProvider(activeProv.id) : null;

  // Frontier label visibility: gold labels suppress each other when too close.
  const visibleFrontierIds = useMemo((): Set<string> => {
    if (!showFrontier) return new Set();
    const CW = 5.3;
    type Box = { id: string; cx: number; cy: number; hw: number; priority: number };
    const boxes: Box[] = FRONTIER.filter(f => !f.noLabel).map(f => {
      const cx = xOf(f.date), cy = yOf(f.index);
      const hw = Math.max(28, f.name.length * (f.big ? 6.2 : CW) + 10);
      const lx = cx + (f.labelDx ?? 8);
      const ly = cy + (f.labelDy ?? -12);
      const labelCx = (f.anchor ?? 'start') === 'end' ? lx - hw / 2 : lx + hw / 2;
      return { id: 'f:' + f.name, cx: labelCx, cy: ly, hw, priority: f.big ? 100 : 60 };
    });
    boxes.sort((a, b) => b.priority - a.priority);
    const kept: Box[] = [];
    for (const box of boxes) {
      if (!kept.some(k => Math.abs(k.cy - box.cy) < 22 && box.cx - box.hw < k.cx + k.hw && k.cx - k.hw < box.cx + box.hw))
        kept.push(box);
    }
    return new Set(kept.map(b => b.id));
  }, [showFrontier]);

  // Provider label placement: never hide — reposition to avoid overlap.
  // Returns a Map<modelId, dy> where dy is added to the dot's spread-y to get label y.
  const providerLabelDy = useMemo((): Map<string, number> => {
    const CW = 5.3;
    type PlacedBox = { cx: number; cy: number; hw: number };
    const placed: PlacedBox[] = [];

    // Seed with visible frontier label positions so provider labels avoid them.
    if (showFrontier) {
      FRONTIER.filter(f => !f.noLabel && visibleFrontierIds.has('f:' + f.name)).forEach(f => {
        const cx = xOf(f.date);
        const hw = Math.max(28, f.name.length * (f.big ? 6.2 : CW) + 10);
        const lx = cx + (f.labelDx ?? 8);
        const ly = yOf(f.index) + (f.labelDy ?? -12);
        const labelCx = (f.anchor ?? 'start') === 'end' ? lx - hw / 2 : lx + hw / 2;
        placed.push({ cx: labelCx, cy: ly, hw });
      });
    }

    const overlaps = (cx: number, cy: number, hw: number) =>
      placed.some(p => Math.abs(p.cy - cy) < 11 && cx - hw < p.cx + p.hw && p.cx - p.hw < cx + hw);

    type Candidate = { id: string; labelCx: number; baseDotY: number; hw: number; priority: number };
    const candidates: Candidate[] = [];
    [...selByProvider.entries()].forEach(([, ms]) => {
      perMonthLine(ms).forEach(m => {
        if (!m.key && !m.star) return;
        const cx = xOf(m.date);
        const spreadY = dotLayout.get(m.id) ?? yOf(m.index);
        const end = cx > 812;
        const hw = Math.max(28, m.name.length * CW + 10);
        const labelCx = end ? cx - 8 - hw / 2 : cx + 8 + hw / 2;
        candidates.push({ id: m.id, labelCx, baseDotY: spreadY, hw, priority: m.star ? 92 : 55 });
      });
    });
    candidates.sort((a, b) => b.priority - a.priority);

    const result = new Map<string, number>();
    for (const c of candidates) {
      let dy = -12;
      for (const attempt of [-12, 14, -26, 28, -40]) {
        if (!overlaps(c.labelCx, c.baseDotY + attempt, c.hw)) { dy = attempt; break; }
      }
      placed.push({ cx: c.labelCx, cy: c.baseDotY + dy, hw: c.hw });
      result.set(c.id, dy);
    }
    return result;
  }, [showFrontier, visibleFrontierIds, selByProvider, dotLayout]);

  const selSig = [...selected].sort().join(',') + '|' + showFrontier;

  /* ── reveal animation ──────────────────────────────────────
     Progressive enhancement: the chart renders FULLY VISIBLE by default,
     so it can never get stuck invisible if a timing API is unavailable.
     When the chart scrolls into view we replay a "draw" (and we replay it
     again whenever the selection changes while it is on screen).
     The draw is intentionally NOT gated by prefers-reduced-motion — it is
     an explicitly requested marketing flourish and the owner browses with
     reduced-motion on (see project history). It stays brief and gentle. ── */
  useEffect(() => {
    const el = rootRef.current?.querySelector('.bnk-catchup__chart-outer');
    if (!el) return;
    let raf = 0;
    const check = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 800;
      setInView(r.top < vh * 0.85 && r.bottom > vh * 0.12);
    };
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(check); };
    check();
    const t1 = window.setTimeout(check, 400);
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver(es => es.forEach(e => setInView(e.isIntersecting)), { threshold: 0.2 })
      : null;
    if (io) io.observe(el);
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    return () => {
      io?.disconnect();
      window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf); window.clearTimeout(t1);
    };
  }, []);

  useEffect(() => {
    if (inView) setAnimEpoch(e => e + 1);
  }, [inView, selSig]);

  // Draw the polylines to their real length. Only runs once revealed
  // (animEpoch>0); otherwise the lines keep their default visible state.
  useLayoutEffect(() => {
    if (animEpoch === 0) return;
    const root = rootRef.current;
    if (!root) return;
    const lines = [...root.querySelectorAll<SVGGeometryElement>('.bnk-catchup__grow')];
    lines.forEach((l, i) => {
      const len = l.getTotalLength?.() || 1000;
      const delay = (Number(l.dataset.delay) || 0) + i * 0.04;
      l.style.transition = 'none';
      l.style.strokeDasharray = String(len);
      l.style.strokeDashoffset = String(len);
      l.getBoundingClientRect(); // force reflow so the reset lands before the transition
      requestAnimationFrame(() => {
        l.style.transition = `stroke-dashoffset 1s cubic-bezier(.45,.05,.2,1) ${delay}s`;
        l.style.strokeDashoffset = '0';
      });
    });
  }, [animEpoch]);

  const revealed = animEpoch > 0;

  /* ── picker filtering ──────────────────────────────────── */
  const q = query.trim().toLowerCase();
  const matches = (m: ModelPoint, p: typeof PROVIDERS[number]) =>
    !q || m.name.toLowerCase().includes(q) || m.family.toLowerCase().includes(q) || p.label.toLowerCase().includes(q);

  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPickerOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickerOpen]);

  /* ── small render helpers ──────────────────────────────── */
  const chip = (id: ProviderId) => {
    const p = PROVIDER_MAP[id];
    const active = providerActive(id);
    return (
      <button key={id} className="bnk-catchup__chip" onClick={() => toggleProvider(id)} aria-pressed={active}
        style={{ borderColor: active ? p.color : undefined, color: active ? p.color : undefined, background: active ? p.color + '1f' : undefined }}>
        <span className="bnk-catchup__chip-dot" style={{ background: active ? p.color : 'var(--bnkc-muted)' }} />
        {p.label}{p.deployMode === 'both' && <span className="bnk-catchup__chip-flash">⚡</span>}
      </button>
    );
  };

  return (
    <section ref={rootRef} className={'bnk-catchup' + (revealed ? ' bnk-catchup--reveal' : '')} aria-labelledby="bnk-catchup-heading">
      <div className="bnk-catchup__wrap">
        <header className="bnk-catchup__header">
          <div className="bnk-catchup__kicker">{c('kicker', 'Artificial Analysis Intelligence Index · Jun 2026')}</div>
          <div className="bnk-catchup__modes">
            <button className={'bnk-catchup__mode-tab' + (mode === 'local' ? ' is-active' : '')} onClick={() => setMode('local')}>
              🖥 {c('modeLocal', 'Local · Free')}
            </button>
            <button className={'bnk-catchup__mode-tab' + (mode === 'openweight' ? ' is-active' : '')} onClick={() => setMode('openweight')}>
              ⚖ {c('modeOpen', 'Open Weight')}
            </button>
            <button className={'bnk-catchup__mode-tab' + (mode === 'roi' ? ' is-active' : '')} onClick={() => setMode('roi')}>
              💰 {c('modeRoi', 'ROI / Break-even')}
            </button>
          </div>
          {mode === 'local' ? (
            <>
              <h2 id="bnk-catchup-heading" className="bnk-catchup__h1">
                {c('localHeadingPre', "Today's gaming PC ")}<em>{c('localHeadingEm', 'beats')}</em>{c('localHeadingPost', " yesterday's data center")}
              </h2>
              <p className="bnk-catchup__sub">{c('localSub', 'Open-weight models running on a 32 GB gaming GPU — no API key, no per-token cost. How close are they to the proprietary frontier?')}</p>
            </>
          ) : mode === 'openweight' ? (
            <>
              <h2 id="bnk-catchup-heading" className="bnk-catchup__h1">
                {c('openHeadingPre', 'Open source is ')}<em>{c('openHeadingEm', 'closing the gap')}</em>
              </h2>
              <p className="bnk-catchup__sub">{c('openSub', 'The full landscape of open-weight models vs. the proprietary frontier — from 9B Qwen to server-scale DeepSeek V4 and GLM-5.2.')}</p>
            </>
          ) : (
            <>
              <h2 id="bnk-catchup-heading" className="bnk-catchup__h1">
                {c('roiHeadingPre', 'At what volume does ')}<em>{c('roiHeadingEm', 'self-hosting pay off')}</em>{c('roiHeadingPost', '?')}
              </h2>
              <p className="bnk-catchup__sub">{c('roiSub', 'Hardware cost + electricity vs. cloud API spend — find the break-even token volume where running your own GPU beats paying per token.')}</p>
            </>
          )}
        </header>

        {/* ── quick-add toolbar (local / openweight modes only) ── */}
        {mode !== 'roi' && <div className="bnk-catchup__filters">
          <button className="bnk-catchup__chip bnk-catchup__chip--frontier" onClick={() => setShowFrontier(v => !v)} aria-pressed={showFrontier}
            style={{ borderColor: showFrontier ? '#e8c46a' : undefined, color: showFrontier ? '#e8c46a' : undefined, background: showFrontier ? '#e8c46a1f' : undefined }}>
            <span className="bnk-catchup__chip-dot" style={{ background: showFrontier ? '#e8c46a' : 'var(--bnkc-muted)' }} />
            {c('frontierChip', 'Frontier')}
          </button>
          <span className="bnk-catchup__filter-sep" />
          {OPEN_PROVIDERS.map(p => chip(p.id))}
          <button className="bnk-catchup__reset" onClick={resetSelected} title={c('resetTitle', 'Reset to defaults')}>↺</button>
          <button className="bnk-catchup__browse" onClick={() => setPickerOpen(true)}>
            <span className="bnk-catchup__browse-plus">＋</span>
            {c('browseAll', 'Select models')} <span className="bnk-catchup__browse-n">{selected.size}</span>
          </button>
        </div>}

        {/* ── ROI / Breakeven chart ── */}
        {mode === 'roi' && (
          <div className="bnk-catchup__chart-outer bnk-catchup__chart-outer--roi" ref={chartRef}>
            {/* Controls — collapsible */}
            <div className="bnk-catchup__roi-filter-bar">
              <button className="bnk-catchup__roi-filter-toggle" onClick={() => setRoiFilterOpen(v => !v)} aria-expanded={roiFilterOpen}>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.3" />
                  <line x1="6" y1="3" x2="6" y2="9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  <line x1="3" y1="6" x2="9" y2="6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" style={{ display: roiFilterOpen ? 'none' : undefined }} />
                </svg>
                {roiFilterOpen ? c('roiFilterHide', 'Hide filters') : c('roiFilterShow', 'Configure')}
              </button>
              {!roiFilterOpen && (
                <span className="bnk-catchup__roi-filter-summary">
                  {roiLocalIds.length} {c('roiLocalModels', 'local models')} · {roiCloudIds.length} {c('roiCloudCompare', 'cloud APIs')} · {amortMonths}{c('roiMoSuffix', ' mo')} {c('roiAmortLabel', 'amortization')}
                </span>
              )}
            </div>
            {roiFilterOpen && (
              <div className="bnk-catchup__roi-controls">
                <div className="bnk-catchup__roi-ctrl-row">
                  <span className="bnk-catchup__roi-ctrl-label">{c('roiAmort', 'Amortize hardware over')}</span>
                  {AMORT_OPTIONS.map(m => (
                    <button key={m} className={'bnk-catchup__roi-amort-btn' + (amortMonths === m ? ' is-active' : '')}
                      onClick={() => setAmortMonths(m)}>{m}{c('roiMoSuffix', ' mo')}</button>
                  ))}
                </div>
                <div className="bnk-catchup__roi-ctrl-row">
                  <span className="bnk-catchup__roi-ctrl-label">{c('roiCloudCompare', 'Cloud API')}</span>
                  {ROI_CLOUD_OPTIONS.map(o => {
                    const active = roiCloudIds.includes(o.id);
                    return (
                      <button key={o.id} className={'bnk-catchup__chip bnk-catchup__chip--roi' + (active ? ' is-active' : '')}
                        aria-pressed={active}
                        style={{ borderColor: active ? o.color : undefined, color: active ? o.color : undefined, background: active ? o.color + '1f' : undefined }}
                        onClick={() => setRoiCloudIds(prev => active ? prev.filter(id => id !== o.id) : [...prev, o.id])}>
                        <span className="bnk-catchup__chip-dot" style={{ background: active ? o.color : 'var(--bnkc-muted)' }} />
                        {o.name} <span className="bnk-catchup__roi-price">${o.costPerM}/M</span>
                      </button>
                    );
                  })}
                </div>
                <div className="bnk-catchup__roi-ctrl-row">
                  <span className="bnk-catchup__roi-ctrl-label">{c('roiLocalModels', 'Local models')}</span>
                  {Object.entries(MODEL_HW).map(([id]) => {
                    const m = MODEL_BY_ID[id];
                    if (!m) return null;
                    const p = PROVIDER_MAP[m.provider];
                    const active = roiLocalIds.includes(id);
                    return (
                      <button key={id} className={'bnk-catchup__chip bnk-catchup__chip--roi' + (active ? ' is-active' : '')}
                        aria-pressed={active}
                        style={{ borderColor: active ? p.color : undefined, color: active ? p.color : undefined, background: active ? p.color + '1f' : undefined }}
                        onClick={() => setRoiLocalIds(prev => active ? prev.filter(i => i !== id) : [...prev, id])}>
                        <span className="bnk-catchup__chip-dot" style={{ background: active ? p.color : 'var(--bnkc-muted)' }} />
                        {m.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SVG Chart */}
            <svg className="bnk-catchup__svg" viewBox="0 0 1000 450" xmlns="http://www.w3.org/2000/svg" role="img"
              aria-label={c('roiChartAriaLabel', 'ROI break-even chart: monthly cost vs token volume for local vs cloud models')}>
              <defs>
                <clipPath id="bnkc-roi-clip">
                  <rect x={RP.x0} y={RP.yTop} width={RP.x1 - RP.x0} height={RP.yBottom - RP.yTop} />
                </clipPath>
              </defs>

              {/* y grid */}
              <g fontFamily="ui-monospace, monospace" fontSize="9.5" fill="#3a3a52" textAnchor="end">
                {ROI_Y_TICKS.map(v => (
                  <g key={v}>
                    <text x="79" y={yC(v) + 3}>${v}</text>
                    <line x1={RP.x0} y1={yC(v)} x2={RP.x1} y2={yC(v)} stroke="#16162a" strokeWidth="1" />
                  </g>
                ))}
                <line x1={RP.x0} y1={yC(0)} x2={RP.x1} y2={yC(0)} stroke="#2a2a3a" strokeWidth="1.5" />
              </g>
              <text x="16" y="215" textAnchor="middle" transform="rotate(-90,16,215)"
                fontFamily="ui-monospace, monospace" fontSize="8.5" fill="#3a3a52" letterSpacing="0.12em">
                {c('roiYAxis', 'MONTHLY COST (USD)')}
              </text>

              {/* x grid + ticks */}
              <g fontFamily="ui-monospace, monospace" fontSize="9.5" fill="#3a3a52" textAnchor="middle">
                {ROI_X_TICKS.map(m => (
                  <g key={m}>
                    <text x={xM(m)} y={RP.yBottom + 18}>{m}M</text>
                    <line x1={xM(m)} y1={RP.yBottom} x2={xM(m)} y2={RP.yBottom + 5} stroke="#2a2a3a" />
                    {m % 10 === 0 && <line x1={xM(m)} y1={RP.yTop} x2={xM(m)} y2={RP.yBottom} stroke="#16162a" strokeWidth="0.6" />}
                  </g>
                ))}
                <text x={(RP.x0 + RP.x1) / 2} y="430" fontFamily="ui-monospace, monospace" fontSize="8.5"
                  fill="#3a3a52" letterSpacing="0.1em">
                  {c('roiXAxis', 'TOKENS / MONTH')}
                </text>
              </g>

              {/* Cloud API cost lines (diagonal, dashed) — labels in legend block below */}
              {roiClouds.map(cloud => {
                const clipM = cloud.costPerM > 0 ? Math.min(ROI_MAX_M, ROI_MAX_COST / cloud.costPerM) : ROI_MAX_M;
                const ex = xM(clipM), ey = yC(clipM * cloud.costPerM);
                return (
                  <g key={cloud.id}>
                    <line x1={xM(0)} y1={yC(0)} x2={ex} y2={ey}
                      stroke={cloud.color} strokeWidth="1.8" strokeDasharray="5,3"
                      clipPath="url(#bnkc-roi-clip)" opacity="0.85" />
                  </g>
                );
              })}

              {/* Cloud API legend — top-left of plot, away from right-edge annotations */}
              <g transform={`translate(${RP.x0 + 8}, ${RP.yTop + 8})`}>
                <rect x="-4" y="-4" rx="4"
                  width={168} height={roiClouds.length * 22 + 12}
                  fill="#071410" fillOpacity="0.88" stroke="#ffffff" strokeWidth="0.4" strokeOpacity="0.1" />
                {roiClouds.map((cloud, i) => (
                  <g key={cloud.id} transform={`translate(0, ${i * 22})`}>
                    <line x1="0" y1="7" x2="14" y2="7" stroke={cloud.color} strokeWidth="1.8" strokeDasharray="5,3" />
                    <text x="18" y="11" fill={cloud.color} fontFamily="Inter, sans-serif" fontSize="9" fontWeight="600">{cloud.name}</text>
                    <text x="18" y="21" fill={cloud.color} fontFamily="ui-monospace, monospace" fontSize="7.5" opacity="0.65">${cloud.costPerM}/M tok</text>
                  </g>
                ))}
              </g>

              {/* Local model TCO lines (horizontal, solid) + break-even diamonds */}
              {roiEntries.map(({ id, model, hw, tco }, ri) => {
                const p = PROVIDER_MAP[model.provider];
                const labelX = RP.x0 - 5;
                const yCval = yC(tco);
                return (
                  <g key={id}>
                    {/* TCO horizontal line */}
                    <line x1={RP.x0} y1={yCval} x2={RP.x1} y2={yCval}
                      stroke={p.color} strokeWidth="2" clipPath="url(#bnkc-roi-clip)" />
                    {/* model name label on the left */}
                    <text x={labelX} y={yCval - 4} textAnchor="end" fill={p.color}
                      fontFamily="Inter, sans-serif" fontSize="8.5" fontWeight="700"
                      stroke="#071410" strokeWidth="3" paintOrder="stroke">
                      {model.name}
                    </text>
                    <text x={labelX} y={yCval + 9} textAnchor="end" fill={p.color}
                      fontFamily="ui-monospace, monospace" fontSize="7" opacity="0.65">
                      {hw.label} · ${Math.round(tco)}/mo
                    </text>
                    {/* break-even diamonds where cloud lines cross this TCO */}
                    {roiClouds.map(cloud => {
                      const beM = tco / cloud.costPerM;
                      if (beM > ROI_MAX_M || beM <= 0.1) return null;
                      const bx = xM(beM);
                      const labelAbove = ri % 2 === 0;
                      return (
                        <g key={cloud.id}>
                          <polygon
                            points={`${bx},${yCval - 7} ${bx + 6},${yCval} ${bx},${yCval + 7} ${bx - 6},${yCval}`}
                            fill={p.color} stroke={cloud.color} strokeWidth="1.2" opacity="0.95"
                          />
                          <text x={bx} y={labelAbove ? yCval - 11 : yCval + 19}
                            textAnchor="middle" fill={cloud.color}
                            fontFamily="ui-monospace, monospace" fontSize="7.5" fontWeight="700">
                            {beM < 1 ? `${(beM * 1000).toFixed(0)}K` : `${beM.toFixed(1)}M`}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              })}

              {/* break-even annotation — bottom-right, clear of cloud legend */}
              <text x={RP.x1 - 6} y={RP.yBottom - 10} textAnchor="end"
                fontFamily="ui-monospace, monospace" fontSize="8" fill="#4fffb0" opacity="0.5" letterSpacing="0.08em">
                {c('roiLegendLocal', '◆ = BREAK-EVEN  ←  LOCAL CHEAPER')}
              </text>
            </svg>

            {/* Hardware info cards */}
            <div className="bnk-catchup__roi-cards">
              {roiEntries.map(({ id, model, hw, tco }) => {
                const p = PROVIDER_MAP[model.provider];
                return (
                  <div key={id} className="bnk-catchup__roi-card" style={{ borderColor: p.color + '44' }}>
                    <div className="bnk-catchup__roi-card-name" style={{ color: p.color }}>{model.name}</div>
                    <div className="bnk-catchup__roi-card-gpu">{hw.label}</div>
                    <div className="bnk-catchup__roi-card-rows">
                      <div className="bnk-catchup__roi-card-row">
                        <span>{c('roiHwAmort', 'Hardware (amortized)')}</span>
                        <b>${Math.round(hw.gpuPriceUsd / amortMonths)}/mo</b>
                      </div>
                      <div className="bnk-catchup__roi-card-row">
                        <span>{c('roiHwPower', 'Electricity')}</span>
                        <b>${hw.monthlyPowerUsd}/mo</b>
                      </div>
                      <div className="bnk-catchup__roi-card-row">
                        <span>{c('roiHwMaint', 'Maintenance')}</span>
                        <b>${hw.monthlyMaintenanceUsd}/mo</b>
                      </div>
                      <div className="bnk-catchup__roi-card-row bnk-catchup__roi-card-row--total">
                        <span>{c('roiTco', 'Total TCO')}</span>
                        <b>${Math.round(tco)}/mo</b>
                      </div>
                    </div>
                    <div className="bnk-catchup__roi-card-breaks">
                      <div className="bnk-catchup__roi-card-breaks-hdr">
                        {c('roiBreakevenHdr', '✓ LOCAL CHEAPER BEYOND:')}
                      </div>
                      {roiClouds.map(cloud => {
                        const beM = tco / cloud.costPerM;
                        const offChart = beM > ROI_MAX_M;
                        const dailyK = offChart ? null : Math.round(beM * 1e6 / 30 / 1000);
                        return (
                          <div key={cloud.id} className="bnk-catchup__roi-card-break">
                            <span style={{ color: cloud.color }}>{cloud.name}</span>
                            <span className="bnk-catchup__roi-card-break-val">
                              <b style={{ color: offChart ? 'var(--bnkc-muted)' : '#4fffb0' }}>
                                {offChart
                                  ? c('roiNeverBreakeven', 'cloud cheaper')
                                  : beM < 1
                                    ? `${(beM * 1000).toFixed(0)}K tok/mo`
                                    : `${beM.toFixed(1)}M tok/mo`
                                }
                              </b>
                              {!offChart && dailyK != null && (
                                <span className="bnk-catchup__roi-card-break-daily">
                                  ≈ {dailyK < 1000 ? `${dailyK}K` : `${(dailyK / 1000).toFixed(1)}M`}/day
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── chart (local / openweight modes) ── */}
        {mode !== 'roi' && <div className="bnk-catchup__chart-outer" ref={chartRef}>
          <button
            className={'bnk-catchup__lbl-toggle' + (showLabels ? ' is-on' : '')}
            onClick={() => setShowLabels(v => !v)}
            title={showLabels ? 'Hide labels' : 'Show labels'}
            aria-pressed={showLabels}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <ellipse cx="8" cy="8" rx="7" ry="4.5" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="8" cy="8" r="2.5" fill="currentColor" />
              {!showLabels && <line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />}
            </svg>
            Labels
          </button>
          <svg className="bnk-catchup__svg" viewBox="0 0 1000 450" xmlns="http://www.w3.org/2000/svg" role="img"
            aria-label={c('chartAriaLabel', 'Intelligence Index over time, open-weight models vs. the proprietary frontier')}>
            <defs>
              <filter id="bnkc-gp"><feGaussianBlur stdDeviation="2.4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
              <clipPath id="bnkc-cc"><rect x="78" y="30" width="882" height="360" /></clipPath>
              <linearGradient id="bnkc-ag" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e8c46a" stopOpacity="0.26" />
                <stop offset="100%" stopColor="#e8c46a" stopOpacity="0" />
              </linearGradient>
              {[...selByProvider.keys()].map(id => {
                const col = PROVIDER_MAP[id].color;
                return (
                  <linearGradient key={id} id={`bnkc-fill-${id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={col} stopOpacity="0.22" />
                    <stop offset="100%" stopColor={col} stopOpacity="0" />
                  </linearGradient>
                );
              })}
            </defs>

            {/* y grid */}
            <g fontFamily="ui-monospace, monospace" fontSize="10" fill="#3a3a52" textAnchor="end">
              {GRID_INDEX.map(v => (
                <g key={v}>
                  <text x="72" y={yOf(v) + 3}>{v}</text>
                  <line x1="78" y1={yOf(v)} x2="960" y2={yOf(v)} stroke="#16162a" strokeWidth="1" />
                </g>
              ))}
            </g>
            <text x="16" y="220" textAnchor="middle" transform="rotate(-90,16,220)" fontFamily="ui-monospace, monospace" fontSize="9" fill="#3a3a52" letterSpacing="0.12em">
              INTELLIGENCE INDEX (AA v4.0)
            </text>

            {/* x ticks */}
            <g fontFamily="ui-monospace, monospace" fontSize="9.5" fill="#3a3a52" textAnchor="middle">
              {YEAR_TICKS.map(tk => (
                <g key={tk.date}>
                  <text x={xOf(tk.date)} y="402">{tk.label}</text>
                  <line x1={xOf(tk.date)} y1="382" x2={xOf(tk.date)} y2="388" stroke="#2a2a3a" />
                </g>
              ))}
            </g>

            {/* "the bar to beat" — Opus 4.5 reference line */}
            <g className="bnk-catchup__target">
              <line x1="78" y1={yOf(OPUS45)} x2="960" y2={yOf(OPUS45)} stroke="#e8c46a" strokeWidth="1" strokeDasharray="3,4" opacity="0.4" />
              <text x="956" y={yOf(OPUS45) - 5} textAnchor="end" fontFamily="ui-monospace, monospace" fontSize="8" fill="#e8c46a" opacity="0.6">
                Opus 4.5 · {OPUS45}
              </text>
            </g>

            {/* proprietary frontier (gold) */}
            {showFrontier && (
              <g key={`frontier-${animEpoch}`}>
                <polygon className="bnk-catchup__area" clipPath="url(#bnkc-cc)" points={areaPts(FRONTIER as unknown as ModelPoint[])} fill="url(#bnkc-ag)" />
                <polyline className="bnk-catchup__grow" data-delay="0" points={pts(FRONTIER as unknown as ModelPoint[])}
                  fill="none" stroke="#e8c46a" strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round" filter="url(#bnkc-gp)" clipPath="url(#bnkc-cc)" />
                {FRONTIER.map((f, i) => {
                  const cx = xOf(f.date), cy = yOf(f.index);
                  const inCompare = compareIds.includes('f:' + f.name);
                  return (
                    <g key={f.name} className="bnk-catchup__dot-g" style={delayVar(0.5 + i * 0.05)}>
                      <circle className={'bnk-catchup__dot' + (inCompare ? ' is-cmp' : '')} cx={cx} cy={cy} r={f.big ? 4.5 : 3.4} fill="#e8c46a"
                        filter="url(#bnkc-gp)" stroke={inCompare ? '#fff' : 'none'} strokeWidth={inCompare ? 1.5 : 0}
                        onMouseEnter={e => onFrontierHover(e, f)} onMouseMove={e => onFrontierHover(e, f)} onMouseLeave={clearHover}
                        onClick={() => toggleCompareFrontier(f)} style={{ cursor: 'pointer' }} />
                      {showLabels && !f.noLabel && visibleFrontierIds.has('f:' + f.name) && (
                        <text className="bnk-catchup__lbl" x={cx + (f.labelDx ?? 8)} y={cy + (f.labelDy ?? -12)} fill="#e8c46a"
                          textAnchor={f.anchor ?? 'start'} fontFamily="Inter, system-ui, sans-serif" fontSize={f.big ? 11 : 9.5} fontWeight={f.big ? 700 : 500}>{f.name}</text>
                      )}
                    </g>
                  );
                })}
              </g>
            )}

            {/* provider lines — dots/lines/labels all use spread positions */}
            {[...selByProvider.entries()].map(([id, ms], pi) => {
              const p = PROVIDER_MAP[id];
              const lineMs = perMonthLine(ms);
              // Build spread-position point strings so lines connect to the offset dots
              const sPts = lineMs.map(m => `${xOf(m.date)},${dotLayout.get(m.id) ?? yOf(m.index)}`).join(' ');
              const sArea = lineMs.length
                ? `${xOf(lineMs[0].date)},${PLOT.yBottom} ${sPts} ${xOf(lineMs[lineMs.length-1].date)},${PLOT.yBottom}`
                : '';
              return (
                <g key={`${id}-${animEpoch}`}>
                  <polygon className="bnk-catchup__area" clipPath="url(#bnkc-cc)" points={sArea} fill={`url(#bnkc-fill-${id})`} />
                  <polyline className="bnk-catchup__grow" data-delay={0.12 + pi * 0.1} points={sPts}
                    fill="none" stroke={p.color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" filter="url(#bnkc-gp)" clipPath="url(#bnkc-cc)" />
                  {lineMs.map((m, i) => {
                    const cx = xOf(m.date);
                    const cy = dotLayout.get(m.id) ?? yOf(m.index); // spread position
                    const end = cx > 812;
                    const dx = end ? -8 : 8;
                    const labelled = m.key || m.star;
                    const inCompare = compareIds.includes(m.id);
                    return (
                      <g key={m.id} className="bnk-catchup__dot-g" style={delayVar(0.6 + pi * 0.1 + i * 0.05)}>
                        {m.star && <circle className="bnk-catchup__dot" cx={cx} cy={cy} r={rOf(m) + 3} fill="none" stroke={p.color} strokeWidth="1" opacity="0.4" />}
                        <circle className={'bnk-catchup__dot' + (inCompare ? ' is-cmp' : '')} cx={cx} cy={cy} r={rOf(m)} fill={p.color}
                          filter={m.star ? 'url(#bnkc-gp)' : undefined} stroke={inCompare ? '#fff' : 'none'} strokeWidth={inCompare ? 1.5 : 0}
                          onMouseEnter={e => { const ci = resolveCompare(m.id); if (ci) onDotHover(e, ci); }} onMouseMove={e => { const ci = resolveCompare(m.id); if (ci) onDotHover(e, ci); }} onMouseLeave={clearHover}
                          onClick={() => toggleCompareModel(m)} style={{ cursor: 'pointer' }} />
                        {showLabels && labelled && (
                          <text x={cx + dx} y={cy + (providerLabelDy.get(m.id) ?? -12)} fill={p.color} textAnchor={end ? 'end' : 'start'}
                            fontFamily="Inter, system-ui, sans-serif" fontSize={m.star ? 10.5 : 9.5} fontWeight={m.star ? 700 : 500}
                            stroke="#071410" strokeWidth="3.5" paintOrder="stroke">
                            {m.name}{m.star ? ' ★' : ''}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {/* comparison panel (top-left box) — click 2 dots to compare */}
            {(() => {
              const W = 400, H = compareModels.length === 2 ? 140 : 100;
              const rows: { label: string; get: (m: CompareItem) => number | undefined }[] = [
                { label: 'Intelligence Index', get: m => m.index },
                { label: 'GPQA Diamond', get: m => m.bench?.gpqa },
                { label: "Humanity's Last Exam", get: m => m.bench?.hle },
                { label: 'Terminal-Bench 2.0', get: m => m.bench?.tbench },
              ];
              if (compareModels.length === 0) {
                return (
                  <g className="bnk-catchup__badges" transform="translate(88, 48)">
                    <rect x="0" y="0" width={W} height="56" rx="6" fill="#071410" stroke="#4fffb0" strokeWidth="0.8" strokeOpacity="0.3" />
                    <text x={W / 2} y="22" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="8" fill="#4fffb0" opacity="0.6" letterSpacing="0.1em">
                      {c('compareHint', 'CLICK 2 DOTS TO COMPARE')}
                    </text>
                    <text x={W / 2} y="38" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="9" fill="#4fffb0" opacity="0.35">
                      {c('compareHintSub', 'Hover any dot for details')}
                    </text>
                  </g>
                );
              }
              // 1 or 2 models
              const models = compareModels;
              return (
                <g className="bnk-catchup__badges" transform="translate(88, 48)">
                  <rect x="0" y="0" width={W} height={H} rx="6" fill="#071410" stroke="#4fffb0" strokeWidth="0.8" strokeOpacity="0.45" />
                  {/* model name headers */}
                  {models.map((m, mi) => {
                    const x = W - 12 - (models.length - 1 - mi) * 130;
                    return (
                      <text key={'h' + m.id} x={x} y="16" textAnchor="end" fill={m.providerColor}
                        fontFamily="Inter, sans-serif" fontSize="9.5" fontWeight="700">
                        {m.name}
                      </text>
                    );
                  })}
                  <line x1="12" y1="24" x2={W - 12} y2="24" stroke="#4fffb0" strokeWidth="0.5" strokeOpacity="0.2" />
                  {rows.map((r, ri) => {
                    const y = 40 + ri * 20;
                    const vals = models.map(m => r.get(m));
                    const maxVal = vals.filter(v => v != null).length > 1 ? Math.max(...vals.filter(v => v != null) as number[]) : undefined;
                    return (
                      <g key={r.label}>
                        <text x="12" y={y} fontFamily="ui-monospace, monospace" fontSize="9" fill="#4fffb0" opacity="0.7">{r.label}</text>
                        {vals.map((v, vi) => {
                          if (v == null) return <text key={vi} x={W - 12 - (vals.length - 1 - vi) * 130} y={y} textAnchor="end" fontFamily="ui-monospace, monospace" fontSize="10" fill="#76768c">—</text>;
                          const isBest = maxVal != null && v === maxVal;
                          return (
                            <text key={vi} x={W - 12 - (vals.length - 1 - vi) * 130} y={y} textAnchor="end"
                              fontFamily="Inter, sans-serif" fontSize="11" fontWeight="700"
                              fill={isBest ? '#4fffb0' : '#e8e8f0'}>{v}</text>
                          );
                        })}
                      </g>
                    );
                  })}
                  <text x="12" y={H - 8} fontFamily="ui-monospace, monospace" fontSize="6.8" fill="#4fffb0" opacity="0.35" letterSpacing="0.06em">
                    {c('compareCaption', 'AA Intelligence Index v4.1 · click dot again to remove')}
                  </text>
                </g>
              );
            })()}
          </svg>

          {/* tooltip */}
          {tip && (() => {
            const m = tip.m;
            const pct = Math.round((m.index / OPUS45) * 100);
            return (
              <div className={'bnk-catchup__tip' + (tip.below ? ' bnk-catchup__tip--below' : '')} style={{ left: tip.x, top: tip.y }}>
                <div className="bnk-catchup__tip-name" style={{ color: m.providerColor }}>{m.name}</div>
                <div className="bnk-catchup__tip-prov">{m.provider} · {m.cloud ? '☁' : '💻'} · {m.date.replace('-', '/')}</div>
                <div className="bnk-catchup__tip-row"><span>{c('tipIndex', 'Intelligence')}</span><b>{m.index}</b></div>
                {m.size && <div className="bnk-catchup__tip-row"><span>{c('tipSize', 'Size')}</span><b>{m.size}</b></div>}
                {m.bench?.gpqa != null && <div className="bnk-catchup__tip-row"><span>GPQA</span><b>{m.bench.gpqa}</b></div>}
                {m.bench?.hle != null && <div className="bnk-catchup__tip-row"><span>HLE</span><b>{m.bench.hle}</b></div>}
                {m.bench?.tbench != null && <div className="bnk-catchup__tip-row"><span>Term-Bench</span><b>{m.bench.tbench}</b></div>}
                {!!m.speed && <div className="bnk-catchup__tip-row"><span>{c('tipSpeed', 'Speed')}</span><b>{Math.round(m.speed)} tok/s</b></div>}
                {m.context != null && <div className="bnk-catchup__tip-row"><span>{c('tipCtx', 'Context')}</span><b>{m.context}K</b></div>}
                {m.cost != null && <div className="bnk-catchup__tip-row"><span>{c('tipCost', 'Cost')}</span><b>{m.cost === 0 ? 'free (local)' : `$${m.cost}/1M`}</b></div>}
                <div className="bnk-catchup__tip-extra">{pct}% {c('tipOfFrontier', 'of Opus 4.5')}</div>
              </div>
            );
          })()}

          {/* legend */}
          <div className="bnk-catchup__legend">
            {showFrontier && (
              <div className="bnk-catchup__legend-item">
                <div className="bnk-catchup__swatch" style={{ background: 'var(--bnkc-prop)', boxShadow: '0 0 6px color-mix(in srgb, var(--bnkc-prop) 33%, transparent)' }} />
                <span className="bnk-catchup__legend-name" style={{ color: 'var(--bnkc-prop)' }}>{c('legendProprietary', 'Proprietary frontier')}</span>
              </div>
            )}
            {[...selByProvider.keys()].map(id => {
              const p = PROVIDER_MAP[id];
              return (
                <div key={id} className="bnk-catchup__legend-item">
                  <div className="bnk-catchup__swatch" style={{ background: p.color, boxShadow: '0 0 6px ' + p.color + '55' }} />
                  <span className="bnk-catchup__legend-name" style={{ color: p.color }}>{p.label}</span>
                  <span style={{ fontSize: '10px', opacity: 0.55 }}>
                    {p.deployMode === 'cloud' ? '☁ cloud' : p.deployMode === 'local' ? '💻 open' : '⚡ cloud+open'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>}

        {/* dynamic context cards */}
        {mode !== 'roi' && activeStar && activeProv && (
          <div className="bnk-catchup__cards">
            <div className="bnk-catchup__card bnk-catchup__card--l">
              <div className="bnk-catchup__card-lbl">{activeStar.name} · {activeProv.deployMode === 'cloud' ? '☁' : '💻'}</div>
              <div className="bnk-catchup__card-val">{activeStar.index}</div>
              <div className="bnk-catchup__card-desc">{c('cardIndexDesc', 'AA Intelligence Index — open-weight, no API key, runs on your own GPU.')}</div>
            </div>
            <div className="bnk-catchup__card bnk-catchup__card--p">
              <div className="bnk-catchup__card-lbl">{c('cardParityLabel', 'vs. Opus 4.5 (40.8)')}</div>
              <div className="bnk-catchup__card-val">{Math.round((activeStar.index / OPUS45) * 100)}%</div>
              <div className="bnk-catchup__card-desc">{c('cardParityDesc', 'of the Opus 4.5 level on the overall index. On coding benchmarks: parity.')}</div>
            </div>
            <div className="bnk-catchup__card bnk-catchup__card--d">
              <div className="bnk-catchup__card-lbl">{c('cardFrontierLabel', 'Proprietary frontier · now')}</div>
              <div className="bnk-catchup__card-val">{FRONTIER_NOW.index}</div>
              <div className="bnk-catchup__card-desc">{c('cardFrontierDesc', 'The data center keeps climbing — but the gap shrinks every quarter.')}</div>
            </div>
          </div>
        )}

        {/* Eigene Messungen statt nur zitierter Indizes: Verweis auf den offenen
            Benchmark (benchmark.securesight.ai), der genau diese Aussage belegt. */}
        <div className="bnk-catchup__bench">
          <div className="bnk-catchup__bench-txt">
            <span className="bnk-catchup__bench-kicker">{c('ownBenchKicker', 'Measured ourselves')}</span>
            <span className="bnk-catchup__bench-desc">{c('ownBenchText', 'How well does local AI really run on affordable AMD hardware? Our open benchmark tests it first-hand: agentic coding in VS Code, image recognition and measured speed, with all raw data.')}</span>
          </div>
          <a
            className="bnk-catchup__bench-btn"
            href={benchmarkUrl(i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {c('ownBenchButton', 'Open the benchmark')}
            <ArrowUpRight size={15} aria-hidden="true" />
          </a>
        </div>

        <p className="bnk-catchup__source">
          {mode === 'roi' ? (
            c('roiSourceNote', 'Hardware prices: Q2 2026 market rates. Electricity: $0.15 / kWh, 24 / 7 utilisation. Cloud pricing: published API rates (blended input/output). Maintenance = IT time + downtime risk estimate.')
          ) : (
            <>
              {c('sourcePrefix', 'Sources:')}{' '}
              <a href="https://artificialanalysis.ai/" target="_blank" rel="noopener noreferrer">Artificial Analysis Intelligence Index v4.0</a>
              {' '}· {c('sourceNote', 'illustrative — milestone models per provider; full catalogue selectable above. ⚡ Mistral: open-weights + cloud · ☁ Gemini, Grok & Kimi: cloud API only')}
            </>
          )}
        </p>
      </div>

      {/* ── model picker (desktop dialog / mobile bottom sheet) ── */}
      {pickerOpen && (
        <div className="bnk-catchup__picker-backdrop" onClick={() => setPickerOpen(false)}>
          <div className="bnk-catchup__picker" role="dialog" aria-modal="true" aria-label={c('compare', 'Compare models')} onClick={e => e.stopPropagation()}>
            <div className="bnk-catchup__picker-head">
              <div>
                <div className="bnk-catchup__picker-title">{c('compare', 'Compare models')}</div>
                <div className="bnk-catchup__picker-count">{c('selectedCount', '{{n}} of {{total}} selected', { n: selected.size, total: modeModels.length })}</div>
              </div>
              <button className="bnk-catchup__picker-x" onClick={() => setPickerOpen(false)} aria-label={c('done', 'Done')}>✕</button>
            </div>

            <div className="bnk-catchup__picker-search">
              <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
                placeholder={c('search', 'Search models, families, providers…')} />
              {selected.size > 0 && (
                <button className="bnk-catchup__picker-clear" onClick={clearSelected}>{c('clearAll', 'Clear all')}</button>
              )}
            </div>

            <div className="bnk-catchup__picker-body">
              {OPEN_PROVIDERS.map(p => {
                const all = modeModels.filter(m => m.provider === p.id);
                const shown = all.filter(m => matches(m, p));
                if (!shown.length) return null;
                const isOpen = expanded.has(p.id) || q.length > 0;
                const selCount = all.filter(m => selected.has(m.id)).length;
                return (
                  <div key={p.id} className="bnk-catchup__pgroup">
                    <div className="bnk-catchup__pgroup-head">
                      <button className="bnk-catchup__pgroup-toggle" onClick={() => setExpanded(s => { const n = new Set(s); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; })}>
                        <span className="bnk-catchup__caret" style={{ transform: isOpen ? 'rotate(90deg)' : '' }}>▸</span>
                        <span className="bnk-catchup__chip-dot" style={{ background: p.color }} />
                        <span className="bnk-catchup__pgroup-name">{p.label}</span>
                        <span className="bnk-catchup__pgroup-tag">{p.deployMode === 'cloud' ? '☁' : p.deployMode === 'both' ? '⚡' : '💻'} {all.length}</span>
                        {selCount > 0 && <span className="bnk-catchup__pgroup-sel" style={{ color: p.color }}>{selCount}</span>}
                      </button>
                      <button className="bnk-catchup__addall" onClick={() => addMany(all.map(m => m.id), selCount < all.length)}
                        style={{ color: p.color, borderColor: p.color + '55' }}>
                        {selCount < all.length ? c('addAll', 'Add all') : c('remove', 'Remove')}
                      </button>
                    </div>

                    {isOpen && [...new Set(all.map(m => m.family))].map(fam => {
                      const fms = shown.filter(m => m.family === fam);
                      if (!fms.length) return null;
                      const famAll = familyIds(p.id, fam);
                      const famOn = familyActive(p.id, fam);
                      return (
                        <div key={fam} className="bnk-catchup__fam">
                          <div className="bnk-catchup__fam-head">
                            <span className="bnk-catchup__fam-name">{fam}</span>
                            <button className="bnk-catchup__fam-add" onClick={() => addMany(famAll, !famOn)}>
                              {famOn ? c('remove', 'Remove') : c('addFamily', '+ family')}
                            </button>
                          </div>
                          {fms.map(m => (
                            <button key={m.id} className={'bnk-catchup__mrow' + (has(m.id) ? ' is-on' : '')} onClick={() => toggleModel(m.id)} aria-pressed={has(m.id)}>
                              <span className="bnk-catchup__check" style={{ borderColor: has(m.id) ? p.color : undefined, background: has(m.id) ? p.color : undefined }}>
                                {has(m.id) ? '✓' : ''}
                              </span>
                              <span className="bnk-catchup__mname">{m.name}{m.star ? ' ★' : ''}</span>
                              <span className="bnk-catchup__mmeta">{m.size ? m.size + ' · ' : ''}{m.date.replace('-', '/')}</span>
                              <span className="bnk-catchup__midx" style={{ color: p.color }}>{m.index}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div className="bnk-catchup__picker-foot">
              <button className="bnk-catchup__done" onClick={() => setPickerOpen(false)}>{c('done', 'Done')}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default LocalAiCatchup;
